import type { AnyFailure } from "@b2bportal/air-booking-api";
import { PassengerType } from "@b2bportal/core-types";
import { getPassengerType } from "@b2bportal/core-utilities";
import type { Traveler } from "@b2bportal/traveler-api";
import { setContextWithKey } from "@checkout/helpers";
import { ParentState } from "@checkout/index";
import type {
  SetCurrentPassengerEvent,
  ValidateAndFetchTripPricingEvent,
} from "@checkout/states/common/PassengerInformation/events";
import { type PassengerError, TravelerErrorModalTypes } from "@checkout/types";
import type { ITraveler } from "@hopper-b2b/types";
import { assign, type DoneInvokeEvent } from "xstate";
import type { PartialParentWithContactAndTraveler } from "../context";

export const actions = {
  onFetchTripPricing: assign(
    (
      ctx: PartialParentWithContactAndTraveler,
      event: DoneInvokeEvent<ValidateAndFetchTripPricingEvent>
    ) => {
      const [, tripPricingEvent] = event.data;
      return setContextWithKey(ctx, "common.tripPricing", tripPricingEvent);
    }
  ),

  setValidated: assign(
    (
      ctx: PartialParentWithContactAndTraveler,
      event: DoneInvokeEvent<AnyFailure>
    ) =>
      setContextWithKey(
        ctx,
        `${ParentState.travelerInformation}.isValidated`,
        true
      )
  ),

  onValidateAndTripPricingError: assign(
    (
      ctx: PartialParentWithContactAndTraveler,
      event: DoneInvokeEvent<PassengerError | AnyFailure>
    ) => {
      const errorData = event.data;
      const isValidateError = (errorData as PassengerError)?.type;
      const tripPricingErrors = (errorData as AnyFailure)?.errors;

      if (isValidateError) {
        return setContextWithKey(
          ctx,
          `${ParentState.travelerInformation}.error`,
          {
            type: (errorData as PassengerError).type,
            data: (errorData as PassengerError).data,
          }
        );
      } else if (tripPricingErrors) {
        return setContextWithKey(
          ctx,
          `${ParentState.travelerInformation}.error`,
          {
            type: TravelerErrorModalTypes.GenericError,
            data: tripPricingErrors,
          }
        );
      }
      return ctx;
    }
  ),

  clearPassengerInformationError: assign(
    (ctx: PartialParentWithContactAndTraveler, _event) => {
      ctx[ParentState.travelerInformation].infantOnlyError = false;
      ctx[ParentState.travelerInformation].error = undefined;
      return ctx;
    }
  ),

  setSingleDefaultAdult: assign((ctx: PartialParentWithContactAndTraveler) => {
    if (ctx[ParentState.travelerInformation].selectedTravelerIds.length > 0) {
      return ctx;
    }

    // Get trip date: try hotels (lodging) first, then fall back to cars
    // Skip flights as they don't use single adult flow
    const tripDate =
      (
        ctx as {
          [ParentState.lodgingShop]?: { fromDate?: string | null };
        }
      )?.[ParentState.lodgingShop]?.fromDate ??
      (
        ctx as {
          groundShop?: {
            selectedVehicle?: { pickUp?: { time?: string } };
          };
        }
      )?.groundShop?.selectedVehicle?.pickUp?.time;

    if (ctx[ParentState.travelerInformation].travelers.length > 0) {
      const firstAdult = ctx[ParentState.travelerInformation].travelers.find(
        (traveler: ITraveler) =>
          getPassengerType({
            dateOfBirth: traveler.dateOfBirth,
            tripDate: tripDate ?? undefined,
          }) === PassengerType.Adult
      );
      if (firstAdult) {
        ctx[ParentState.travelerInformation].selectedTravelerIds = [
          firstAdult.id,
        ];
      }
    }
    return ctx;
  }),

  dismissNumPassengerAlert: assign(
    (
      ctx: PartialParentWithContactAndTraveler,
      _event: SetCurrentPassengerEvent
    ) =>
      setContextWithKey(
        ctx,
        `${ParentState.travelerInformation}.numPassengerAlertDismissed`,
        true
      )
  ),

  /**
   * Re-select a traveler after update without toggling.
   * This ensures the traveler stays selected when UPDATE_TRAVELER completes.
   * Used in onDone handler which receives DoneInvokeEvent<Traveler> instead of SelectTravelerEvent.
   */
  selectTravelerAfterUpdate: assign(
    (
      ctx: PartialParentWithContactAndTraveler,
      event: DoneInvokeEvent<Traveler>
    ) => {
      const updatedTraveler = event.data;
      const travelerInformationCtx = ctx[ParentState.travelerInformation];
      const { selectedTravelerIds } = travelerInformationCtx;

      // If already selected, keep it selected (don't toggle)
      if (selectedTravelerIds.includes(updatedTraveler.id)) {
        return ctx;
      }

      // Add to selected list (multi-select mode for activities/flights)
      const newTravelerInfoCtx = {
        ...travelerInformationCtx,
        selectedTravelerIds: [...selectedTravelerIds, updatedTraveler.id],
      };

      return {
        ...ctx,
        [ParentState.travelerInformation]: newTravelerInfoCtx,
      };
    }
  ),
};
